import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import {
  CoderbyteWorkspaceFieldsFragment,
  useCoderbyteWorkspaceAdminPasswordLazyQuery,
  useSetCoderbyteWorkspaceAdminPasswordMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  TextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { formatTimestamp } from 'helpers/date';
import {
  findAdminPasswordFailures,
  generateAdminPassword,
} from '../coderbyteAdminPassword.helpers';
import styles from './CoderbyteCredentials.module.scss';

const dataTestId = 'coderbyte-admin-password';

type AdminPasswordCardProps = {
  companyId: number;
  workspace: CoderbyteWorkspaceFieldsFragment;
  onChanged: () => void;
};

/**
 * The password for this workspace's Coderbyte admin account.
 *
 * Coderbyte emails a temporary one when the workspace is created, which has to
 * be reset on their site. The working password is kept here so it lives
 * somewhere encrypted and shared rather than in one person's notes.
 *
 * It is never part of the workspace query — revealing it is a separate,
 * on-demand fetch that the server logs.
 */
export function AdminPasswordCard({
  companyId,
  workspace,
  onChanged,
}: AdminPasswordCardProps) {
  const { handleMsgType } = useNotification();
  const [isEditing, setIsEditing] = useState(!workspace.adminPasswordSetAt);
  const [revealed, setRevealed] = useState<string | null>(null);

  const [reveal, { loading: revealing }] =
    useCoderbyteWorkspaceAdminPasswordLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: result =>
        setRevealed(result.coderbyteWorkspaceAdminPassword ?? null),
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Could not reveal the password',
          message: error.message,
          dismissable: true,
        });
      },
    });

  if (isEditing) {
    return (
      <AdminPasswordForm
        companyId={companyId}
        onCancel={
          workspace.adminPasswordSetAt ? () => setIsEditing(false) : undefined
        }
        onSaved={() => {
          setIsEditing(false);
          setRevealed(null);
          onChanged();
        }}
      />
    );
  }

  return (
    <div className={styles.card} data-testid={dataTestId}>
      <p className={styles.cardTitle}>Admin password</p>
      <InfoItem
        label="Set"
        value={formatTimestamp(workspace.adminPasswordSetAt)}
        valueNote="Used with the admin email to sign in to Coderbyte."
      />
      {revealed && <InfoItem label="Password" value={revealed} hasCopyButton />}

      <div className={styles.buttonRow}>
        {revealed ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => setRevealed(null)}
          >
            Hide
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="medium"
            disabled={revealing}
            onClick={() => reveal({ variables: { companyId } })}
            data-testid={`${dataTestId}__reveal`}
          >
            {revealing ? 'Revealing…' : 'Reveal password'}
          </Button>
        )}
        <Button
          variant="secondary"
          size="medium"
          onClick={() => setIsEditing(true)}
          data-testid={`${dataTestId}__replace`}
        >
          Replace password
        </Button>
      </div>
    </div>
  );
}

type AdminPasswordFormProps = {
  companyId: number;
  onSaved: () => void;
  onCancel?: () => void;
};

function AdminPasswordForm({
  companyId,
  onSaved,
  onCancel,
}: AdminPasswordFormProps) {
  const { handleMsgType } = useNotification();
  const [password, setPassword] = useState('');

  const failures = findAdminPasswordFailures(password);
  const isValid = password.length > 0 && failures.length === 0;

  const [setAdminPassword, { loading }] =
    useSetCoderbyteWorkspaceAdminPasswordMutation({
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Admin password saved.',
          dismissable: true,
        });
        onSaved();
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Could not save the admin password',
          message: error.message,
          dismissable: true,
        });
      },
    });

  return (
    <div className={styles.card}>
      <p className={styles.cardTitle}>Admin password</p>
      <p className={styles.caption}>
        This must match the password on the Coderbyte account. Saving it here
        does not change it in Coderbyte — set it there first, then record it.
      </p>

      {/* Shown rather than masked: it is being transcribed into Coderbyte, and
          a typo in a masked field is the likeliest way to end up locked out. */}
      <TextInputField
        id="coderbyteAdminPassword"
        label="Password"
        value={password}
        onChange={value => setPassword(value)}
        autoComplete="off"
      />

      {password.length > 0 && failures.length > 0 && (
        <p className={styles.caption}>Password must {failures.join(', ')}.</p>
      )}

      <div className={styles.buttonRow}>
        <Button
          variant="primary"
          size="medium"
          disabled={loading || !isValid}
          onClick={() =>
            setAdminPassword({
              variables: { companyId, adminPassword: password },
            })
          }
          data-testid={`${dataTestId}__save`}
        >
          {loading ? 'Saving…' : 'Save password'}
        </Button>
        <Button
          variant="secondary"
          size="medium"
          disabled={loading}
          onClick={() => setPassword(generateAdminPassword())}
          data-testid={`${dataTestId}__generate`}
        >
          Generate
        </Button>
        {onCancel && (
          <Button
            variant="secondary"
            size="medium"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
